import React from 'react';
import { useQuery } from '@apollo/client';
import { Box, Typography, List, ListItem, ListItemText, ListItemSecondaryAction, IconButton, Paper, Divider, CircularProgress } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import i18next from 'i18next';
import { formatDate } from 'components/DateTime';
import { getFullBackendUrl } from 'helper/download';

import { LIST_OFFER_FILES_ADMIN_QUERY } from 'pages/offers/gql';
import { ListOfferFilesAdminQuery, ListOfferFilesAdminQueryVariables } from '__generated__/graphql';

interface AdminOfferDocumentViewerProps {
  offerId: number;
}

export const AdminOfferDocumentViewer: React.FC<AdminOfferDocumentViewerProps> = ({ offerId }) => {
  const { data, loading, error } = useQuery<ListOfferFilesAdminQuery, ListOfferFilesAdminQueryVariables>(LIST_OFFER_FILES_ADMIN_QUERY, {
    variables: { offerId },
    errorPolicy: 'all',
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={100}>
        <CircularProgress size={24} />
        <Typography variant="body2" sx={{ ml: 2 }}>
          {i18next.t('admin-offer-documents-loading')}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="body2" color="error">
          {i18next.t('admin-offer-documents-load-error')}: {error.message}
        </Typography>
      </Box>
    );
  }

  const files = data?.listOfferFilesAdmin || [];

  if (files.length === 0) {
    return (
      <Box>
        <Typography variant="body2" color="textSecondary">
          {i18next.t('admin-offer-documents-empty')}
        </Typography>
      </Box>
    );
  }

  const handleDownload = (fileId: number, filename: string) => {
    window.open(getFullBackendUrl(`/backend/spacefiles/download/${fileId}`), '_blank');
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {i18next.t('admin-offer-documents-title')}
      </Typography>
      <Paper variant="outlined">
        <List dense>
          {files.map((file, index: number) => (
            <React.Fragment key={file.id}>
              {index > 0 && <Divider />}
              <ListItem>
                <DescriptionIcon color="action" sx={{ mr: 2 }} />
                <ListItemText
                  primary={file.originalFilename}
                  secondary={
                    <Typography variant="caption" color="textSecondary">
                      {i18next.t('admin-offer-documents-uploaded-at')} {formatDate(file.createdAt)}
                    </Typography>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => handleDownload(file.id, file.originalFilename)}
                    size="small"
                    title={String(i18next.t('download'))}
                  >
                    <DownloadIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      </Paper>
    </Box>
  );
};

export default AdminOfferDocumentViewer;